// Updated Notifications.tsx component
import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BellDot, Loader2, X } from "lucide-react";
import { useTranslations, useLocale } from "next-intl";
import { useActivityLogActions } from "@/hooks/useActivityLogActions";
import { handleError } from "@/utils/errorHandler";
import { ActivityLog } from "@/server-side/types/activityLogs";
import {
  formatActivityMessage,
  getActivityEntityMeta,
} from "@/utils/activityMessageFormatter";
import { DateTime } from "luxon";
import { useAuth } from "@/contexts/AuthContext";
import { useRouter } from "next/navigation";
import { useWebSocket } from "@/contexts/WebSocketContext";
import { ACTIVITY_LOG_WEBSOCKET_EVENTS } from "@/constants/activityLogEvents";
import { useUnseenNotificationCount } from "@/contexts/UnseenNotificationCountContext";

const Notifications: React.FC = () => {
  const t = useTranslations("Navigation.Notifications");
  const locale = useLocale();
  const { user } = useAuth();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);
  const {
    getRecentActivityLogs,
    getUnviewedActivityCount,
    markAllActivityLogsAsViewed,
  } = useActivityLogActions();
  const { lastMessage, connectionState } = useWebSocket();
  const prevConnectionStateRef = useRef(connectionState);
  const loadRequestIdRef = useRef(0);

  const { unseenCount, setUnseenCount } = useUnseenNotificationCount();

  // The badge total comes from its own endpoint rather than from the loaded
  // page, which only ever holds a handful of entries and would cap the count.
  const loadUnseenCount = useCallback(async () => {
    await getUnviewedActivityCount({
      onSuccess: (count: number) => setUnseenCount(count),
      onError: (error) => {
        handleError(error, {
          component: "Notifications",
          action: "getUnviewedActivityCount",
        });
      },
    });
  }, [getUnviewedActivityCount, setUnseenCount]);

  const loadNotifications = useCallback(async () => {
    const requestId = ++loadRequestIdRef.current;
    setIsLoading(true);
    await getRecentActivityLogs({
      onSuccess: (data: ActivityLog[]) => {
        if (requestId !== loadRequestIdRef.current) return;
        const sortedLogs = data.sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        );
        setNotifications(sortedLogs);
        setHasLoaded(true);
        setIsLoading(false);
      },
      onError: (error) => {
        if (requestId !== loadRequestIdRef.current) return;
        handleError(error, {
          component: "Notifications",
          action: "getNotifications",
        });
        setIsLoading(false);
      },
    });
  }, [getRecentActivityLogs]);

  // Dismissing the bell acknowledges every unread entry, not just the few the
  // dropdown loaded. Marking only the loaded ones would leave an older backlog
  // permanently stuck in the badge, since the dropdown never loads far enough
  // back to reach it.
  const markNotificationsAsViewed = useCallback(async () => {
    if (unseenCount === 0) return;

    setNotifications((prev) =>
      prev.map((notification) =>
        notification.viewedByCurrentUser
          ? notification
          : { ...notification, viewedByCurrentUser: true },
      ),
    );
    setUnseenCount(0);

    await markAllActivityLogsAsViewed({
      onError: (error) => {
        handleError(error, {
          component: "Notifications",
          action: "markAllActivityLogsAsViewed",
        });
        // Put the real number back if the acknowledgement did not persist
        loadUnseenCount();
      },
    });
  }, [unseenCount, markAllActivityLogsAsViewed, setUnseenCount, loadUnseenCount]);

  // Fire markNotificationsAsViewed only on close, not on initial mount
  useEffect(() => {
    if (isOpen) {
      wasOpen.current = true;
    } else if (wasOpen.current) {
      markNotificationsAsViewed();
      wasOpen.current = false;
    }
  }, [isOpen, markNotificationsAsViewed]);

  // Load initial notifications when user is authenticated with finalized profile
  useEffect(() => {
    if (user?.sub && user?.irf) {
      loadNotifications();
      loadUnseenCount();
    } else {
      setNotifications([]);
      setHasLoaded(false);
      setUnseenCount(0);
    }
  }, [user?.sub, user?.irf, loadNotifications, loadUnseenCount, setUnseenCount]);

  // Listen for WebSocket messages and refresh notifications
  useEffect(() => {
    if (
      lastMessage &&
      ACTIVITY_LOG_WEBSOCKET_EVENTS.includes(lastMessage.type as any)
    ) {
      loadNotifications();
      loadUnseenCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);

  // Reload notifications on WebSocket reconnect to catch any events missed during disconnect
  useEffect(() => {
    if (
      prevConnectionStateRef.current !== 'connected' &&
      connectionState === 'connected' &&
      hasLoaded
    ) {
      loadNotifications();
      loadUnseenCount();
    }
    prevConnectionStateRef.current = connectionState;
  }, [connectionState, hasLoaded, loadNotifications, loadUnseenCount]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleViewAllClick = () => {
    router.push(`/${locale}/activity-logs`);
    setIsOpen(false);
  };

  const formatTimestamp = (timestamp: string): string => {
    return DateTime.fromISO(timestamp).toRelative({ locale }) || "Unknown time";
  };

  const isConnected = connectionState === "connected";

  return (
    <div ref={dropdownRef} className="relative inline-block">
      <button
        onClick={handleToggleDropdown}
        className="p-2 text-gray-300 hover:text-white relative transition-transform duration-300 hover:scale-110"
        aria-label={t("NotificationsButton")}
      >
        <BellDot className="w-5 h-5" />
        <span
          className={`absolute bottom-1 left-1 w-2 h-2 rounded-full border border-gray-900 ${
            isConnected ? "bg-green-500" : "bg-red-500"
          }`}
        />
        {unseenCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center px-1">
            {unseenCount > 9 ? "9+" : unseenCount}
          </span>
        )}
      </button>

      {/* Notifications - Mobile Full Screen Overlay and Desktop Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Mobile Version - Full Screen Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-40 sm:hidden"
              onClick={() => setIsOpen(false)}
            />

            <motion.div
              initial={{ opacity: 0, y: -100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -100 }}
              transition={{ duration: 0.3, type: "spring", damping: 25 }}
              className="fixed top-0 left-0 right-0 bg-gray-900 border-b border-gray-700 shadow-xl z-50 sm:hidden max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Mobile Header */}
              <div className="flex items-center justify-between px-4 py-4 border-b border-gray-700">
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {t("Title")}
                  </h3>
                  {unseenCount > 0 && (
                    <p className="text-sm text-gray-400">
                      {t("UnreadCount", { count: unseenCount })}
                    </p>
                  )}
                  <div className="relative group/status mt-1">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${isConnected ? "bg-green-500" : "bg-red-500"}`} />
                      <span className={`text-xs ${isConnected ? "text-green-400" : "text-red-400"}`}>
                        {t(isConnected ? "ConnectionActive" : "ConnectionInactive")}
                      </span>
                    </div>
                    {!isConnected && (
                      <div className="absolute left-0 top-full mt-1 px-2 py-1 bg-gray-950 text-xs text-gray-300 rounded shadow-lg whitespace-nowrap opacity-0 group-hover/status:opacity-100 transition-opacity z-10">
                        {t("ConnectionInactiveTooltip")}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                  aria-label="Close notifications"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Mobile Content */}
              <div className="flex-1 overflow-y-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center p-12">
                    <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="text-center p-12 text-gray-400">
                    <BellDot className="w-16 h-16 mx-auto mb-6 opacity-50" />
                    <p className="text-base">{t("NoNotifications")}</p>
                  </div>
                ) : (
                  <div>
                    {notifications.map((notification) => {
                      const isUnseen = !notification.viewedByCurrentUser;
                      const entity = getActivityEntityMeta(notification);
                      return (
                        <div
                          key={notification.id}
                          className={`px-4 py-5 border-b border-gray-700/50 last:border-b-0 ${
                            isUnseen
                              ? "bg-blue-500/5 border-l-4 border-l-blue-500"
                              : ""
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            <div
                              className={`p-2 rounded-lg mt-1 flex-shrink-0 ${entity.chip}`}
                            >
                              <entity.Icon className={`w-5 h-5 ${entity.icon}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-3">
                                <p
                                  className={`text-base leading-relaxed ${
                                    isUnseen ? "text-white" : "text-gray-300"
                                  }`}
                                >
                                  {formatActivityMessage(
                                    notification,
                                    locale,
                                    t,
                                  )}
                                </p>
                                {isUnseen && (
                                  <div className="w-3 h-3 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                                )}
                              </div>
                              <p className="text-sm text-gray-500 mt-3">
                                {formatTimestamp(notification.timestamp)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Mobile Footer */}
              {notifications.length > 0 && (
                <div className="px-4 py-3 border-t border-gray-700 bg-gray-800/50 flex-shrink-0">
                  <button
                    onClick={handleViewAllClick}
                    className="text-sm text-blue-400 hover:text-blue-300 transition-colors font-medium"
                  >
                    {t("ViewAll")}
                  </button>
                </div>
              )}
            </motion.div>

            {/* Desktop Version - Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="absolute right-0 mt-2 w-80 bg-gray-800 border border-gray-700 rounded-xl shadow-xl overflow-hidden z-50 hidden sm:block md:w-96"
            >
              {/* Desktop Header */}
              <div className="px-4 py-3 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-white">{t("Title")}</h3>
                  <div className="relative group/status">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? "bg-green-500" : "bg-red-500"}`} />
                      <span className={`text-xs ${isConnected ? "text-green-400" : "text-red-400"}`}>
                        {t(isConnected ? "ConnectionActive" : "ConnectionInactive")}
                      </span>
                    </div>
                    {!isConnected && (
                      <div className="absolute right-0 top-full mt-1 px-2 py-1 bg-gray-950 text-xs text-gray-300 rounded shadow-lg whitespace-nowrap opacity-0 group-hover/status:opacity-100 transition-opacity z-10">
                        {t("ConnectionInactiveTooltip")}
                      </div>
                    )}
                  </div>
                </div>
                {unseenCount > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    {t("UnreadCount", { count: unseenCount })}
                  </p>
                )}
              </div>

              {/* Desktop Content */}
              <div className="max-h-72 overflow-y-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="text-center p-8 text-gray-400">
                    <BellDot className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="text-sm">{t("NoNotifications")}</p>
                  </div>
                ) : (
                  <div className="py-1">
                    {notifications.map((notification) => {
                      const isUnseen = !notification.viewedByCurrentUser;
                      const entity = getActivityEntityMeta(notification);
                      return (
                        <div
                          key={notification.id}
                          className={`px-4 py-4 hover:bg-gray-700/50 transition-colors border-b border-gray-700/50 last:border-b-0 ${
                            isUnseen
                              ? "bg-blue-500/5 border-l-2 border-l-blue-500"
                              : ""
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className={`p-1.5 rounded-lg mt-0.5 flex-shrink-0 ${entity.chip}`}
                            >
                              <entity.Icon className={`w-4 h-4 ${entity.icon}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p
                                  className={`text-sm leading-relaxed ${
                                    isUnseen ? "text-white" : "text-gray-300"
                                  }`}
                                >
                                  {formatActivityMessage(
                                    notification,
                                    locale,
                                    t,
                                  )}
                                </p>
                                {isUnseen && (
                                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" />
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-2">
                                {formatTimestamp(notification.timestamp)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Desktop Footer */}
              {notifications.length > 0 && (
                <div className="px-4 py-2 border-t border-gray-700">
                  <button
                    onClick={handleViewAllClick}
                    className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    {t("ViewAll")}
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Notifications;
